import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  BarChart,
  CurrencyValue,
  DisplaySwitch,
  Flex,
  LineChart,
  formatDatetime,
  formatNumber
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

export default function HistoricTabs() {
  const theme = useTheme();
  const [activeDisplayOption, setActiveDisplayOption] = useState(
    'treasury_balance_usd'
  );
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const displayOptions = useMemo(
    () => [
      { key: 'treasury_balance_usd', value: 'Treasury Balance' },
      { key: 'sky_buyback', value: 'SKY Buybacks' }
    ],
    []
  );

  const [data, error] = useApi({
    url: '/treasury/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const filteredDataLine = useMemo(() => {
    if (!data?.line) return [];
    if (activeDisplayOption !== 'treasury_balance_usd') {
      return data.line.filter(
        (entry) => new Date(entry.date) >= new Date('2024-09-01')
      );
    }
    return data.line;
  }, [data?.line, activeDisplayOption]);

  const filteredDataBar = useMemo(() => {
    if (!data?.bar) return [];

    return data.bar.filter(
      (entry) => new Date(entry.date) >= new Date('2024-09-01')
    );
  }, [data?.bar]);

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const datasets = useMemo(() => {
    if (activeDisplayOption === 'sky_buyback') {
      return [
        {
          xField: 'date',
          yField: 'sky_buyback',
          label: 'Sky Buybacks',
          backgroundColor: theme.colors.chartUp
        }
      ];
    }
    return [
      {
        xField: 'date',
        yField: 'sky_inflows',
        label: 'To Treasury',
        backgroundColor: theme.colors.chartUp
      },
      {
        xField: 'date',
        yField: 'sky_outflows',
        label: 'From Treasury',
        backgroundColor: theme.colors.chartDown
      }
    ];
  }, [theme.colors, activeDisplayOption]);

  const yScaleValueFormatter = useMemo(() => {
    return (value) => {
      switch (activeDisplayOption) {
        case 'sky_balance':
        case 'sky_buyback':
        case 'flow':
          return `${formatNumber(value)}`;

        default:
          return `$${formatNumber(value)}`;
      }
    };
  }, [activeDisplayOption]);

  const valueFormatter = useMemo(() => {
    function formatValue(value) {
      switch (activeDisplayOption) {
        case 'sky_balance':
        case 'sky_buyback':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('SKY'),
                iconSize: '3'
              }}
            />
          );
        case 'flow':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('SKY'),
                iconSize: '0'
              }}
            />
          );
        default:
          return `$${formatNumber(Number(value))}`;
      }
    }

    return formatValue;
  }, [activeDisplayOption]);
  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (filteredDataLine.length > 0 || filteredDataBar.length > 0) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />

        {activeDisplayOption === 'flow' ||
        activeDisplayOption === 'sky_buyback' ? (
          <BarChart
            key={activeDisplayOption}
            data={filteredDataBar}
            datasets={datasets}
            valueFormatter={valueFormatter}
            yScaleValueFormatter={yScaleValueFormatter}
            timeFilter={timeFilter}
            subValueFormatter={subValueFormatter}
            config={config}
          />
        ) : (
          <LineChart
            key={activeDisplayOption}
            data={filteredDataLine}
            datasets={[{ xField: 'date', yField: activeDisplayOption }]}
            valueFormatter={valueFormatter}
            yScaleValueFormatter={yScaleValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        )}
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
